import { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ptBR from 'date-fns/locale/pt-BR';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';

function CadastroAviso() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [dataInicio, setDataInicio] = useState(new Date());
  const [dataFim, setDataFim] = useState(new Date());
  const [arquivo, setArquivo] = useState(null);
  const [arquivoAtual, setArquivoAtual] = useState(null);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [carregando, setCarregando] = useState(false);
  const [carregandoDados, setCarregandoDados] = useState(false);

  useEffect(() => {
    const carregarDados = async () => {
      if (!id) return;

      setCarregandoDados(true);
      try {
        const response = await axios.get(`https://avisos.tibmacae.org.br/api.php?id=${id}`);
        const aviso = response.data;
        console.log(aviso);

        if (aviso) {
          setTitulo(aviso.titulo || '');
          setDescricao(aviso.descricao || '');
          setDataInicio(aviso.data_inicio ? new Date(aviso.data_inicio) : new Date());
          setDataFim(aviso.data_fim ? new Date(aviso.data_fim) : new Date());
          setArquivoAtual(aviso.arquivo || null);
        } else {
          setMensagem({ 
            tipo: 'error', 
            texto: 'Aviso não encontrado' 
          });
        }
      } catch (erro) {
        console.error('Erro ao carregar aviso:', erro);
        setMensagem({ 
          tipo: 'error', 
          texto: 'Erro ao carregar aviso. Por favor, tente novamente.' 
        });
      } finally {
        setCarregandoDados(false);
      }
    };

    carregarDados();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!titulo || !descricao) {
      setMensagem({ tipo: 'error', texto: 'Preencha todos os campos obrigatórios' });
      return;
    }

    if (dataFim < dataInicio) {
      setMensagem({ tipo: 'error', texto: 'A data final não pode ser anterior à data inicial' });
      return;
    }

    setCarregando(true);

    try {
      let arquivoUrl = arquivoAtual;
      
      if (arquivo) {
        const formData = new FormData();
        formData.append('arquivo', arquivo);
        formData.append('titulo', titulo);

        const maxRetries = 3;
        let retryCount = 0;
        let success = false;

        while (retryCount < maxRetries && !success) {
          try {
            const response = await axios.post('https://avisos.tibmacae.org.br/upload.php', formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
              timeout: 600000,
              onUploadProgress: (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                console.log(`Upload progress: ${percentCompleted}%`);
              }
            });

            arquivoUrl = response.data.url;
            success = true;
          } catch (erro) {
            retryCount++;
            console.error(`Tentativa ${retryCount} falhou:`, erro);
            
            if (retryCount === maxRetries) {
              setMensagem({ 
                tipo: 'error', 
                texto: 'Erro ao fazer upload do arquivo após várias tentativas. Por favor, tente novamente mais tarde.' 
              });
              setCarregando(false);
              return;
            }
            
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
      }

      const aviso = {
        titulo,
        descricao,
        data_inicio: dataInicio.toISOString(),
        data_fim: dataFim.toISOString(),
        arquivo: arquivoUrl,
      };

      if (id) {
        // Atualizar aviso existente
        await axios.put('https://avisos.tibmacae.org.br/api.php', {
          ...aviso,
          id: parseInt(id)
        });
        setMensagem({ tipo: 'success', texto: 'Aviso atualizado com sucesso!' });
      } else {
        // Criar novo aviso
        await axios.post('https://avisos.tibmacae.org.br/api.php', aviso);
        setMensagem({ tipo: 'success', texto: 'Aviso cadastrado com sucesso!' });
      }

      // Limpar formulário
      if (!id) {
        setTitulo('');
        setDescricao('');
        setDataInicio(new Date());
        setDataFim(new Date());
        setArquivo(null);
        setArquivoAtual(null);
      }

      // Redirecionar após 2 segundos
      setTimeout(() => {
        navigate('/avisos');
      }, 2000);

    } catch (erro) {
      console.error('Erro completo:', erro);
      const mensagemErro = erro.response?.data?.error || 
                          erro.response?.data?.details?.join('\n') || 
                          'Erro ao salvar aviso. Por favor, tente novamente.';
      
      setMensagem({ 
        tipo: 'error', 
        texto: mensagemErro
      });
    } finally {
      setCarregando(false);
    }
  };

  if (carregandoDados) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {id ? 'Editar Aviso' : 'Novo Aviso'}
        </Typography>

        {mensagem.texto && (
          <Alert severity={mensagem.tipo} sx={{ mb: 2 }}>
            {mensagem.texto}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Título"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Descrição"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            margin="normal"
            required
            multiline
            rows={4}
          />

          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2, mb: 2 }}>
              <DatePicker
                label="Data de Início"
                value={dataInicio}
                onChange={(newValue) => setDataInicio(newValue)}
                sx={{ flex: 1 }}
              />
              <DatePicker
                label="Data de Término"
                value={dataFim}
                onChange={(newValue) => setDataFim(newValue)}
                sx={{ flex: 1 }}
              />
            </Stack>
          </LocalizationProvider>

          <Box sx={{ mt: 2, mb: 2 }}>
            <input
              accept="image/*,.pdf,.doc,.docx,.mov,.mp4"
              style={{ display: 'none' }}
              id="arquivo-upload"
              type="file"
              onChange={(e) => setArquivo(e.target.files[0])}
            />
            <label htmlFor="arquivo-upload">
              <Button variant="outlined" component="span">
                Anexar Arquivo
              </Button>
            </label>
            {arquivo && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Novo arquivo selecionado: {arquivo.name}
              </Typography>
            )}
            {arquivoAtual && !arquivo && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Arquivo atual: {arquivoAtual.split('/').pop()}
              </Typography>
            )}
          </Box>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            disabled={carregando}
          >
            {carregando ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                {id ? 'Atualizando...' : 'Enviando...'}
              </>
            ) : (
              id ? 'Atualizar Aviso' : 'Cadastrar Aviso'
            )}
          </Button>
        </form>
      </Paper>
    </Container>
  );
}

export default CadastroAviso; 